
import { Card, CardContent } from "@/components/ui/card";
import { useI18n } from '../i18n/i18n';
import { useSEO } from '../hooks/useSEO';

const PrivacyPolicyPage = () => {
  const { t } = useI18n();
  
  // SEO Configuration
  const seoContent = useSEO('legal', 'privacy');

  return (
    <div className="fade-in relative py-12 bg-[#F9FAFB]">
      {seoContent}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-orange-100/30 to-transparent rounded-bl-full -z-10" />
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-gradient-to-tr from-orange-100/30 to-transparent rounded-tr-full -z-10" />

      <div className="container mx-auto px-4 md:px-6">
        <Card className="max-w-4xl mx-auto">
          <CardContent className="p-6 md:p-8">
            <h1 className="text-3xl font-bold text-neutral-800 mb-6">
              {t('privacy.title')}
            </h1>
            <p className="text-sm text-neutral-600 mb-8">
              {t('privacy.effectiveDate')}: 04/05/2025
            </p>

            <div className="prose prose-neutral max-w-none">
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.introduction.title')}</h2>
              <p className="mb-4">{t('privacy.introduction.welcome')}</p>
              <p className="mb-4">{t('privacy.introduction.commitment')}</p>
              <p className="mb-6">{t('privacy.introduction.acceptance')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.collection.title')}</h2>
              <p className="mb-4">{t('privacy.collection.description')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.collection.calculators.title')}</h3>
              <p className="mb-4">{t('privacy.collection.calculators.content')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.collection.automatic.title')}</h3>
              <p className="mb-4">{t('privacy.collection.automatic.content')}</p>
              <ul className="list-disc pl-6 mb-6">
                <li className="mb-2">{t('privacy.collection.automatic.technical')}</li>
                <li className="mb-2">{t('privacy.collection.automatic.usage')}</li>
              </ul>
              <p className="mb-6">{t('privacy.collection.automatic.note')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.usage.title')}</h2>
              <p className="mb-4">{t('privacy.usage.description')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.usage.services.title')}</h3>
              <ul className="list-disc pl-6 mb-6">
                <li className="mb-2">{t('privacy.usage.services.calculators')}</li>
                <li className="mb-2">{t('privacy.usage.services.compatibility')}</li>
              </ul>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.usage.improvement.title')}</h3>
              <ul className="list-disc pl-6 mb-6">
                <li className="mb-2">{t('privacy.usage.improvement.analysis')}</li>
                <li className="mb-2">{t('privacy.usage.improvement.monitoring')}</li>
              </ul>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.usage.security.title')}</h3>
              <ul className="list-disc pl-6 mb-6">
                <li className="mb-2">{t('privacy.usage.security.protection')}</li>
                <li className="mb-2">{t('privacy.usage.security.compliance')}</li>
              </ul>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.legal.title')}</h2>
              <p className="mb-4">{t('privacy.legal.description')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.legal.legitimateInterests.title')}</h3>
              <p className="mb-4">{t('privacy.legal.legitimateInterests.content')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.legal.consent.title')}</h3>
              <p className="mb-4">{t('privacy.legal.consent.content')}</p>
              
              <p className="mb-6">{t('privacy.legal.calculatorData')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.sharing.title')}</h2>
              <p className="mb-4">{t('privacy.sharing.introduction')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.sharing.serviceProviders.title')}</h3>
              <p className="mb-4">{t('privacy.sharing.serviceProviders.content')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.sharing.legalRequirements.title')}</h3>
              <p className="mb-4">{t('privacy.sharing.legalRequirements.content')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.sharing.aggregateData.title')}</h3>
              <p className="mb-4">{t('privacy.sharing.aggregateData.content')}</p>
              
              <p className="mb-6">{t('privacy.sharing.calculatorData')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.cookies.title')}</h2>
              <p className="mb-4">{t('privacy.cookies.description')}</p>
              <p className="mb-4">{t('privacy.cookies.whatAre')}</p>
              <p className="mb-4">{t('privacy.cookies.types')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.cookies.essential.title')}</h3>
              <p className="mb-4">{t('privacy.cookies.essential.content')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.cookies.performance.title')}</h3>
              <p className="mb-4">{t('privacy.cookies.performance.content')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('privacy.cookies.management.title')}</h3>
              <p className="mb-6">{t('privacy.cookies.management.content')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.security.title')}</h2>
              <p className="mb-4">{t('privacy.security.measures')}</p>
              <p className="mb-6">{t('privacy.security.limitations')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.retention.title')}</h2>
              <p className="mb-4">{t('privacy.retention.calculatorData')}</p>
              <p className="mb-6">{t('privacy.retention.automaticData')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.transfers.title')}</h2>
              <p className="mb-6">{t('privacy.transfers.content')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.userRights.title')}</h2>
              <p className="mb-4">{t('privacy.userRights.description')}</p>
              
              <ul className="list-disc pl-6 mb-6">
                <li className="mb-2">{t('privacy.userRights.access')}</li>
                <li className="mb-2">{t('privacy.userRights.rectification')}</li>
                <li className="mb-2">{t('privacy.userRights.deletion')}</li>
                <li className="mb-2">{t('privacy.userRights.restriction')}</li>
                <li className="mb-2">{t('privacy.userRights.objection')}</li>
                <li className="mb-2">{t('privacy.userRights.withdraw')}</li>
              </ul>
              
              <p className="mb-4">{t('privacy.userRights.limitations')}</p>
              <p className="mb-6">{t('privacy.userRights.complaint')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.minors.title')}</h2>
              <p className="mb-6">{t('privacy.minors.content')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.thirdPartyLinks.title')}</h2>
              <p className="mb-6">{t('privacy.thirdPartyLinks.content')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.changes.title')}</h2>
              <p className="mb-6">{t('privacy.changes.content')}</p>
              
              <h2 className="text-xl font-semibold mt-8 mb-4">{t('privacy.contact.title')}</h2>
              <p className="mb-6">{t('privacy.contact.content')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
